import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Upload, FileText, FileImage, Download, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const TIPI = [
  { v: "consenso_privacy", l: "Consenso privacy (cartaceo)" },
  { v: "documento_identita", l: "Documento d'identità" },
  { v: "ricetta_medica", l: "Ricetta medica" },
  { v: "altro", l: "Altro" },
];
const TIPO_LABEL = Object.fromEntries(TIPI.map((t) => [t.v, t.l]));

function humanSize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function DocumentiCliente({ clienteId }) {
  const [docs, setDocs] = useState([]);
  const [tipo, setTipo] = useState("consenso_privacy");
  const [uploading, setUploading] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/clienti/${clienteId}/documenti`);
    setDocs(data);
  }, [clienteId]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", tipo);
      await api.post(`/clienti/${clienteId}/documenti`, fd);
      toast.success("Documento caricato");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documenti/${d.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename || "documento";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Impossibile scaricare il documento");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/documenti/${toDelete.id}`);
      toast.success("Documento eliminato");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6">
        <div className="flex items-center gap-2 mb-1">
          <Upload className="h-5 w-5 text-[#1E40AF]" />
          <h3 className="font-head text-lg font-semibold text-slate-900">Carica documento</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">Allega documenti alla scheda cliente (es. consenso privacy cartaceo scansionato). Formati: PDF, JPG, PNG, WEBP — max 15 MB.</p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="space-y-1.5 flex-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo documento</label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="documento-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIPI.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onFile} className="hidden" data-testid="documento-file-input" />
          <Button data-testid="documento-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#1E40AF] hover:bg-[#1D4ED8] h-10">
            {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Caricamento…</> : <><Upload className="h-4 w-4 mr-2" /> Seleziona file</>}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700">Documenti allegati ({docs.length})</span>
        </div>
        {docs.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">Nessun documento allegato.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {docs.map((d) => {
              const isImg = (d.content_type || "").startsWith("image/");
              return (
                <div key={d.id} data-testid={`documento-${d.id}`} className="px-5 py-3.5 flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${isImg ? "bg-sky-100 text-sky-700" : "bg-red-100 text-red-700"}`}>
                    {isImg ? <FileImage className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{d.original_filename}</div>
                    <div className="text-xs text-slate-500">{humanSize(d.size)} · {d.created_at ? new Date(d.created_at).toLocaleDateString("it-IT") : ""} · {d.created_by}</div>
                  </div>
                  <Badge variant="secondary" className="shrink-0 hidden sm:inline-flex">{TIPO_LABEL[d.tipo] || d.tipo}</Badge>
                  <button data-testid={`documento-download-${d.id}`} onClick={() => download(d)} className="p-2 rounded-lg text-slate-400 hover:text-[#1E40AF] hover:bg-slate-50" title="Scarica">
                    <Download className="h-4 w-4" />
                  </button>
                  <button data-testid={`documento-delete-${d.id}`} onClick={() => setToDelete(d)} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" title="Elimina">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="flex items-center gap-2 text-xs text-slate-400">
        <ShieldCheck className="h-4 w-4" /> I documenti sono archiviati in modo sicuro e accessibili solo agli utenti autenticati. Ogni caricamento è tracciato nel log attività.
      </p>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il documento?</AlertDialogTitle>
            <AlertDialogDescription>Il documento "{toDelete?.original_filename}" verrà rimosso dalla scheda cliente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-documento" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
